import React, { useEffect, useState } from "react";
import { Loader2, Download, Search } from "lucide-react";
import { toast } from "sonner";
import api from "../../console/api";
import { money } from "./FinanceDashboard";

const TYPES = [
  ["", "All movements"], ["sale", "Sales"], ["payment", "Payments in"], ["refund", "Refunds"],
  ["credit_note", "Credit notes"], ["manual_income", "Other income"], ["manual_expense", "Expenses"],
];
const TYPE_STYLE = {
  sale: "bg-[#EFE6F2] text-[#3E1F46]", payment: "bg-[#E4F0E8] text-[#2F6B4F]", refund: "bg-[#FBF0F0] text-[#A33030]",
  credit_note: "bg-[#FBF0F0] text-[#A33030]", manual_income: "bg-[#E4F0E8] text-[#2F6B4F]", manual_expense: "bg-[#FBF0F0] text-[#A14C4C]",
};

export function FinanceLedger({ query, refreshKey, onOpenOrder }) {
  const [type, setType] = useState("");
  const [q, setQ] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const qs = `${query}${query ? "&" : ""}type=${type}&q=${encodeURIComponent(q)}`;
    api.get(`/admin/finance/ledger?${qs}`)
      .then((r) => alive && setData(r.data))
      .catch((e) => toast.error(e?.response?.data?.detail || "Couldn't load the ledger."))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [query, type, q, refreshKey]);

  const exportCsv = async () => {
    try {
      const r = await api.get(`/admin/finance/ledger.csv?${query}${query ? "&" : ""}type=${type}&q=${encodeURIComponent(q)}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data); const a = document.createElement("a"); a.href = url; a.download = "heritage-ledger.csv"; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch { toast.error("Export failed."); }
  };

  return (
    <section className="bg-white border" style={{ borderColor: "#E8DFCB" }} data-testid="finance-ledger">
      <div className="flex flex-wrap items-center gap-2 p-3 border-b" style={{ borderColor: "#F3EBDD" }}>
        <div className="flex flex-wrap gap-1">
          {TYPES.map(([v, l]) => (
            <button key={v} type="button" onClick={() => setType(v)} data-testid={`ledger-type-${v || "all"}`}
              className={`h-8 px-2.5 text-[10px] tracking-[0.18em] uppercase font-semibold border ${type === v ? "bg-[#3E1F46] text-white border-[#3E1F46]" : "border-[#E8DFCB] text-[#5A4565]"}`}>{l}</button>
          ))}
        </div>
        <div className="relative ml-auto">
          <Search className="w-3.5 h-3.5 absolute left-2 top-2.5 text-[#9C8B73]" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search ledger…" data-testid="ledger-search"
            className="h-8 pl-7 pr-2 border text-xs w-56" style={{ borderColor: "#E8DFCB" }} />
        </div>
        <button type="button" onClick={exportCsv} className="h-8 px-3 inline-flex items-center gap-1.5 border text-xs font-semibold" style={{ borderColor: "#2F4A2A55", color: "#2F4A2A" }} data-testid="ledger-export"><Download className="w-3.5 h-3.5" /> CSV</button>
      </div>

      {data && (
        <div className="flex flex-wrap gap-x-6 gap-y-1 px-4 py-2 text-xs text-[#7A6A55] border-b" style={{ borderColor: "#F3EBDD" }} data-testid="ledger-totals">
          <span>{data.count} line{data.count === 1 ? "" : "s"}</span>
          <span>Sales <strong className="text-[#3E1F46]">{money(data.totals.sales)}</strong></span>
          <span>In <strong className="text-[#2F6B4F]">{money(data.totals.in)}</strong></span>
          <span>Out <strong className="text-[#A33030]">{money(data.totals.out)}</strong></span>
        </div>
      )}

      {loading && !data ? <div className="py-16 text-center"><Loader2 className="w-5 h-5 animate-spin inline text-[#6E1E3A]" /></div> : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[9px] tracking-[0.25em] uppercase text-[#6E1E3A] text-left">
                <th className="px-4 py-2">Date</th><th className="px-2 py-2">Type</th><th className="px-2 py-2">Order</th><th className="px-2 py-2">Customer</th><th className="px-2 py-2">Category / note</th>
                <th className="px-2 py-2 text-right">Sale</th><th className="px-2 py-2 text-right">In</th><th className="px-4 py-2 text-right">Out</th>
              </tr>
            </thead>
            <tbody>
              {(data?.lines || []).map((l) => (
                <tr key={l.id} className="border-t hover:bg-[#FBF6E9]" style={{ borderColor: "#F3EBDD" }} data-testid={`ledger-line-${l.id}`}>
                  <td className="px-4 py-2 whitespace-nowrap text-[#7A6A55]">{String(l.date || "").slice(0, 10)}</td>
                  <td className="px-2 py-2"><span className={`px-1.5 py-0.5 text-[9px] tracking-[0.18em] uppercase font-semibold ${TYPE_STYLE[l.type] || "bg-[#F3EBDD]"}`}>{l.type.replace("_", " ")}</span></td>
                  <td className="px-2 py-2 whitespace-nowrap">
                    {l.order_id ? <button type="button" onClick={() => onOpenOrder(l.order_id)} className="text-[#6E1E3A] font-semibold hover:underline" data-testid={`ledger-open-${l.id}`}>{l.order_number || l.order_id.slice(0, 8)}</button> : <span className="text-[#9C8B73]">—</span>}
                  </td>
                  <td className="px-2 py-2 max-w-[220px] truncate">{l.customer_name || l.email || <span className="text-[#9C8B73]">—</span>}{l.kind_label && <span className="block text-[10px] text-[#9C8B73]">{l.kind_label}</span>}</td>
                  <td className="px-2 py-2 max-w-[300px] truncate text-[#5A4565]">{l.category}{l.note ? ` · ${l.note}` : ""}</td>
                  <td className="px-2 py-2 text-right text-[#3E1F46]">{l.direction === "sale" ? money(l.amount) : ""}</td>
                  <td className="px-2 py-2 text-right text-[#2F6B4F]">{l.direction === "in" ? money(l.amount) : ""}</td>
                  <td className="px-4 py-2 text-right text-[#A33030]">{l.direction === "out" ? money(l.amount) : ""}</td>
                </tr>
              ))}
              {data && data.lines.length === 0 && <tr><td colSpan={8} className="px-4 py-10 text-center italic text-[#9C8B73]">No movements match.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
